// Copyright (c) Docugami, Inc. All rights reserved.

import { ComponentId } from "sdv/engine/Component";
import { ControlComponent, ControlType, postOrderTraversal } from "sdv/components/ControlComponent";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { ISystem, Queries } from "../engine/Engine";
import { IResources } from "../Resources";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { TokenRangeInfoComponent } from "sdv/components/TokenRangeInfoComponent";
import { TokenRange } from "networking/api";
import { TokenRangeUtils } from "utils/TokenRangeUtils";

interface UpdateInfo {
    entity: Entity,
    containedStructuralCount: number;
}

export class TokenRangeInfoSystem implements ISystem {
    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager

    queries: Queries = {
        tokenRangeInfos: { types: [ComponentId.TokenRange, ComponentId.TokenRangeInfo, ComponentId.Renderable] },
        rootControl: { types: [ComponentId.RootControl, ComponentId.Active] }
    }

    update() {
        const root = this.queries.rootControl.query!.single();
        if (!root) {
            return;
        }

        const toUpdate: UpdateInfo[] = []
        for (const entity of this.queries.tokenRangeInfos.query!.entities) {
            const tokenRange = entity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!;
            const tokenRangeInfo = entity.getComponent<TokenRangeInfoComponent>(ComponentId.TokenRangeInfo)!;

            if (!this.resources.check("rootControl") && tokenRange.tokenRange.fromTok === tokenRangeInfo.evaluatedFromTok && tokenRange.tokenRange.toTok === tokenRangeInfo.evaluatedToTok) {
                continue;
            }

            delete tokenRangeInfo.fromTokHyperlineId;
            delete tokenRangeInfo.toTokHyperlineId;
            delete tokenRangeInfo.fromTokStructuralId;
            delete tokenRangeInfo.toTokStructuralId;
            delete tokenRangeInfo.isSubStructural;

            tokenRangeInfo.evaluatedFromTok = tokenRange.tokenRange.fromTok;
            tokenRangeInfo.evaluatedToTok = tokenRange.tokenRange.toTok;

            toUpdate.push({
                entity: entity,
                containedStructuralCount: 0
            });
        }

        const checkToken = (tokenRange: TokenRange, token: number) => token >= tokenRange.fromTok && token < tokenRange.toTok;
        const updateInfo = (toUpdate: UpdateInfo, entity: Entity): boolean => {
            const tokenRange = toUpdate.entity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            const tokenRangeInfo = toUpdate.entity.getComponent<TokenRangeInfoComponent>(ComponentId.TokenRangeInfo)!;
            const entityControlComp = entity.getComponent<ControlComponent>(ComponentId.Control)!;
            const entityTokenRange: TokenRange = {
                fromTok: entityControlComp.fromTok,
                toTok: entityControlComp.toTok
            }

            if (TokenRangeUtils.rangesOverlap(entityTokenRange, tokenRange)) {
                if (TokenRangeUtils.rangeIsContained(entityTokenRange, tokenRange)) {
                    toUpdate.containedStructuralCount++;
                }

                if (checkToken(entityTokenRange, tokenRange.fromTok)) {
                    if (entity.hasComponent(ComponentId.Hyperline)) {
                        tokenRangeInfo.fromTokHyperlineId = tokenRangeInfo.fromTokHyperlineId || entity.name;
                    }

                    tokenRangeInfo.fromTokStructuralId = tokenRangeInfo.fromTokStructuralId || entity.name;
                }

                if (checkToken(entityTokenRange, tokenRange.toTok - 1)) {
                    if (entity.hasComponent(ComponentId.Hyperline)) {
                        tokenRangeInfo.toTokHyperlineId = tokenRangeInfo.toTokHyperlineId || entity.name;
                    }

                    tokenRangeInfo.toTokStructuralId = tokenRangeInfo.toTokStructuralId || entity.name;
                }
            }

            tokenRangeInfo.isSubStructural = !toUpdate.containedStructuralCount;
            return !!tokenRangeInfo.fromTokHyperlineId && !!tokenRangeInfo.toTokHyperlineId;
        }

        const iter = postOrderTraversal(root, this.entities);
        let result = iter.next();
        while (!!result.value && !!toUpdate.length) {
            const entity: Entity = result.value;
            const controlComp = entity.getComponent<ControlComponent>(ComponentId.Control)!;
            if (controlComp.type !== ControlType.SmallChunk || entity.hasComponent(ComponentId.Hyperline)) {
                for (let i = toUpdate.length - 1; i >= 0; --i) {
                    const complete = updateInfo(toUpdate[i], entity);
                    if (complete) {
                        toUpdate.splice(i, 1);
                    }
                }
            }

            result = iter.next();
        }
    }
}
